import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNativeMessages } from '@/hooks/useNativeMessages';
import { VersionSyncInfo } from '@/types/nativeBridge.types';
import { isNativeWebView, requestVersionSyncStatus } from '@/utils/nativeBridge';

const VersionSyncContext = createContext<boolean | undefined>(undefined);

export const useVersionSynced = (): boolean => {
  const context = useContext(VersionSyncContext);
  if (context === undefined) {
    throw new Error('useVersionSynced must be used within a VersionSyncProvider');
  }
  return context;
};

// Asks native for its version-sync status once this listener is mounted
// (request/response, not a native-initiated broadcast) — this is the part
// that avoids the race where native fires `version_sync_status` before the
// WebView's JS has finished booting and the message is lost with no
// listener to catch it. Defaults to "not synced" inside the native WebView
// until a reply arrives — current (pre-this-feature) native builds simply
// never reply, which is exactly the right read for them. Outside the
// WebView (desktop/browser testing) there's nothing to sync against, so it
// defaults to true and never gates anything.
export const VersionSyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [versionSynced, setVersionSynced] = useState(() => !isNativeWebView());

  const handleVersionSyncStatus = useCallback((info: VersionSyncInfo) => {
    setVersionSynced(!!info.versionSynced);
  }, []);

  useNativeMessages(undefined, undefined, undefined, undefined, undefined, handleVersionSyncStatus);

  useEffect(() => {
    if (isNativeWebView()) {
      requestVersionSyncStatus();
    }
  }, []);

  return (
    <VersionSyncContext.Provider value={versionSynced}>
      {children}
    </VersionSyncContext.Provider>
  );
};
